// src/utils/windowCloseComparison.js
// Shared, conservative checks for customer-facing completed-window results.
// The final price comes from the backend's timestamped scheduled-close quote;
// all percentages and sats are recomputed from this client's recorded trades.
import { pickBuyPrice, toUTCDate, STRIKE_FEE_RATE } from "./smartTimingImpact";

export const normalizedWindowKey = (planId, windowStart) => {
  const date = toUTCDate(windowStart);
  const iso = Number.isFinite(date.getTime())
    ? date.toISOString()
    : String(windowStart || "");
  return `${planId == null ? "no-plan" : String(planId)}:${iso}`;
};

const closeEnough = (actual, expected, tolerance) =>
  Number.isFinite(actual) && Math.abs(actual - expected) <= tolerance;

/**
 * Validated, independently recalculated comparison for one completed window.
 * null means insufficient/inconsistent historical evidence, NOT a loss.
 *
 * pct: BTC-weighted average execution-price improvement, in percentage POINTS.
 * gainPct/deltaSats: fee-adjusted improvement vs scheduled DCA, separately
 * calculated from the original trade amounts and the same 0.99% assumed
 * Strike benchmark fee used by the backend. These remain estimates when a
 * historical user's real Strike fee differed from that assumption.
 */
export const getVerifiedWindowComparison = (w, result) => {
  if (!w?.isSmartTiming || !w?.hasRealWindowStart || !Array.isArray(w.trades)) return null;
  if (!result || result.benchmark !== "scheduled_window_end") return null;
  // A plan's current cadence is not proof of a historical window's cadence.
  if (!result.window_end_source || result.window_end_source === "current_freq_fallback") return null;
  if (!w.trades.length || w.trades.some((t) => t?.smart_timing !== true)) return null;
  if (normalizedWindowKey(w.planId, w.windowStart) !==
      normalizedWindowKey(result.plan_id, result.window_start)) return null;

  const scheduledPrice = Number(result.scheduled_price);
  const closeMs = toUTCDate(result.completed_at).getTime();
  const quoteMs = toUTCDate(result.scheduled_price_at).getTime();
  const startMs = toUTCDate(w.windowStart).getTime();
  if (!Number.isFinite(scheduledPrice) || scheduledPrice <= 0 ||
      !Number.isFinite(closeMs) || !Number.isFinite(quoteMs) ||
      !Number.isFinite(startMs) || closeMs <= startMs || closeMs > Date.now() ||
      quoteMs < closeMs || quoteMs > closeMs + 15 * 60000) return null;

  // Reject a backend-inferred close when our observed cadence contradicts it.
  if (w.windowEndSource?.startsWith("observed")) {
    const observedEnd = toUTCDate(w.windowEnd).getTime();
    if (Number.isFinite(observedEnd) && Math.abs(observedEnd - closeMs) > 5 * 60000) return null;
  }

  let grossUSD = 0;
  let feeUSD = 0;
  let actualSats = 0;
  let weightedPriceTotal = 0;
  let btcTotal = 0;
  for (const t of w.trades) {
    const gross = Number(t.usd_cost);
    const fee = Number(t.fee);
    const btc = Number(t.btc_amt);
    const price = pickBuyPrice(t);
    if (!Number.isFinite(gross) || gross <= 0 ||
        !Number.isFinite(fee) || fee < 0 || fee >= gross ||
        !Number.isFinite(btc) || btc <= 0 ||
        !Number.isFinite(price) || price <= 0) return null;
    grossUSD += gross;
    feeUSD += fee;
    btcTotal += btc;
    actualSats += btc * 1e8;
    weightedPriceTotal += price * btc;
  }
  const backendCount = Number(result.trade_count);
  if (!Number.isInteger(backendCount) || backendCount !== w.trades.length) return null;
  if (!closeEnough(Number(result.gross_usd), grossUSD, 0.01 * w.trades.length + 0.01)) return null;
  if (!closeEnough(Number(result.actual_sats), actualSats, 2)) return null;
  if (!closeEnough(Number(w.totalBtc), btcTotal, 1e-8) ||
      !closeEnough(Number(w.totalUsd), grossUSD, 0.01)) return null;

  const avgBuyPrice = weightedPriceTotal / btcTotal;
  if (!closeEnough(Number(w.avgBuyPrice), avgBuyPrice, 0.01)) return null;

  const netUSD = (grossUSD - feeUSD) * (1 - STRIKE_FEE_RATE);
  const scheduledSats = netUSD / scheduledPrice * 1e8;
  if (!(netUSD > 0) || !(scheduledSats > 0)) return null;
  const deltaSats = actualSats - scheduledSats;
  const gainPct = (actualSats / scheduledSats - 1) * 100;
  // The backend currently rounds these for transport. Validate them, but
  // never take the backend's success/failure assessment on trust.
  if (!closeEnough(Number(result.scheduled_sats), scheduledSats, 2) ||
      !closeEnough(Number(result.delta_sats), deltaSats, 2) ||
      !closeEnough(Number(result.gain_pct), gainPct, 0.006)) return null;

  return {
    scheduledPrice,
    avgBuyPrice,
    pct: ((scheduledPrice - avgBuyPrice) / scheduledPrice) * 100,
    gainPct,
    deltaSats: Math.round(deltaSats),
    actualSats: Math.round(actualSats),
    scheduledSats: Math.round(scheduledSats),
    actualSatsFloat: actualSats,
    scheduledSatsFloat: scheduledSats,
    tradeCount: w.trades.length,
  };
};

// A missing scheduled quote must not make us celebrate an older winner if
// a NEWER, locally identifiable completed window exists for that plan.
export const isNewestKnownCompletedWindow = (groups, result) => {
  if (!result) return false;
  const candidateKey = normalizedWindowKey(result.plan_id, result.window_start);
  const newest = (groups || []).find((group) =>
    group?.isSmartTiming && String(group.planId) === String(result.plan_id) &&
    Number.isFinite(toUTCDate(group.windowEnd).getTime()) &&
    toUTCDate(group.windowEnd).getTime() <= Date.now()
  );
  return !newest || normalizedWindowKey(newest.planId, newest.windowStart) === candidateKey;
};

// Only summarize a fully validated set; never silently omit losing or
// unverifiable completed windows and then label the remainder "lifetime".
export const summarizeVerifiedWindows = (groups, performance) => {
  const windows = performance?.windows;
  if (performance?.benchmark !== "scheduled_window_end" ||
      !Array.isArray(windows) || !windows.length) return null;
  const groupsByKey = new Map(
    (groups || []).map((g) => [normalizedWindowKey(g.planId, g.windowStart), g])
  );
  const comparisons = [];
  for (const result of windows) {
    const group = groupsByKey.get(normalizedWindowKey(result.plan_id, result.window_start));
    const comparison = getVerifiedWindowComparison(group, result);
    if (!comparison) return null;
    comparisons.push(comparison);
  }
  const actualSats = comparisons.reduce((sum, w) => sum + w.actualSatsFloat, 0);
  const scheduledSats = comparisons.reduce((sum, w) => sum + w.scheduledSatsFloat, 0);
  if (!(scheduledSats > 0)) return null;
  const gainPct = (actualSats / scheduledSats - 1) * 100;
  const deltaSats = actualSats - scheduledSats;
  const positiveWindows = comparisons.filter((w) => w.deltaSats > 0).length;
  // Also cross-check the server's complete-window summary for missing windows.
  const summary = performance.summary;
  if (!summary || Number(summary.completed_windows) !== windows.length ||
      !closeEnough(Number(summary.actual_sats), actualSats, 2 * windows.length) ||
      !closeEnough(Number(summary.scheduled_sats), scheduledSats, 2 * windows.length) ||
      !closeEnough(Number(summary.delta_sats), deltaSats, 2 * windows.length) ||
      !closeEnough(Number(summary.gain_pct), gainPct, 0.006)) return null;
  return {
    completed_windows: windows.length,
    positive_windows: positiveWindows,
    actual_sats: Math.round(actualSats),
    scheduled_sats: Math.round(scheduledSats),
    delta_sats: Math.round(deltaSats),
    gain_pct: gainPct,
    best_window_pct: Math.max(...comparisons.map((w) => w.gainPct)),
    has_shareable_result: gainPct >= 0.25 && positiveWindows > 0,
  };
};